import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  Validators,
} from '@angular/forms';
import { MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent } from '@angular/material/legacy-autocomplete';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';

import { State } from '®app/ngrx/state';
import { CurrentUserAndPermissions } from '®shared/current-user/current-user-and-permissions';
import { UserRecord } from '®shared/user';

import { UserSearchState, selectUserSearchState } from './list-owners.state';
import {
  UserSearchClearedAction,
  UserSearchInitiatedAction,
} from './user-search.actions';

export const DEBOUNCE_SEARCH_CHANGES_MS = 750;
export const SEARCH_TERM_MAX_LENGTH_CHARS = 100;

export const MAX_USERS_LIMIT = 5;

@Component({
  selector: 'rl-user-search-field',
  templateUrl: './user-search-field.component.html',
})
export class UserSearchFieldComponent implements OnChanges, OnDestroy, OnInit {
  MAX_USERS_LIMIT = MAX_USERS_LIMIT;

  @Input() currentUser: CurrentUserAndPermissions;
  @Input() disabled: boolean;
  @Output() userSelected = new EventEmitter<UserRecord>();

  userCtrl: UntypedFormControl;

  private searchStateSubscription: Subscription;
  searchState: UserSearchState;

  constructor(
    private cd: ChangeDetectorRef,
    private formBuilder: UntypedFormBuilder,
    private store: Store<State>,
  ) {}

  createForm() {
    this.userCtrl = this.formBuilder.control('', [
      Validators.pattern(/\S/),
      Validators.maxLength(SEARCH_TERM_MAX_LENGTH_CHARS),
    ]);
  }

  ngOnDestroy() {
    this.searchStateSubscription.unsubscribe();
  }

  ngOnInit() {
    this.searchStateSubscription = this.store
      .select(selectUserSearchState)
      .subscribe(searchState => {
        this.searchState = searchState;
        this.cd.markForCheck();
      });

    this.createForm();

    this.userCtrl.valueChanges
      .pipe(
        debounceTime(DEBOUNCE_SEARCH_CHANGES_MS),
        distinctUntilChanged(),
        map(searchTerm => (searchTerm ? searchTerm.trim() : null)),
      )
      .forEach(searchTerm => {
        if (this.disabled) {
          return;
        }

        const action = searchTerm
          ? new UserSearchInitiatedAction({
              searchTerm,
              limit: MAX_USERS_LIMIT,
            })
          : new UserSearchClearedAction();

        this.store.dispatch(action);
      });
  }

  ngOnChanges(changes?: SimpleChanges) {
    if (
      changes &&
      changes.disabled &&
      this.userCtrl &&
      this.userCtrl.disabled !== this.disabled
    ) {
      if (this.disabled) {
        this.userCtrl.disable();
      } else {
        this.userCtrl.enable();
      }
    }
  }

  /**
   * When an autocomplete user is selected, clear the input field and emit the user to `userSelected`
   * @param {MatAutocompleteSelectedEvent} $event The event containing the selected user
   */
  selectUser($event: MatAutocompleteSelectedEvent): void {
    if (!this.disabled) {
      this.userSelected.emit($event.option.value);
    }

    // never display the selected object - after selection, we clear the input
    this.userCtrl.reset();
  }
}
